// src/pages/Facturacion.js

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import Select from 'react-select';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import '../styles/Facturacion.css';

const METODOS_PAGO = ['Efectivo', 'Nequi', 'Daviplata', 'Transferencia'];
const CLIENTE_POR_DEFECTO = 'Consumidor final';

const dinero = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;

// Dos renglones solo se funden si son el mismo ítem Y llevan exactamente
// los mismos ingredientes. Antes se fundían siempre y la personalización
// del segundo se perdía en silencio.
const firmaIngredientes = (ings = []) =>
  ings.map((i) => `${i.ingredient_id}:${i.amount}`).sort().join('|');

let contadorLinea = 0;
const nuevaLinea = () => `l${Date.now()}_${contadorLinea++}`;

/* Control de cantidad: − [n] +.
   El input se autoselecciona al enfocarlo, así teclear reemplaza el valor
   en vez de pegarse al que ya estaba — que es el error típico bajo presión. */
function Cantidad({ valor, onCambio, etiqueta, id, decimal = false, min = 1 }) {
  const parsear = (t) => {
    const n = decimal ? parseFloat(t) : parseInt(t, 10);
    return Number.isFinite(n) ? Math.max(min, n) : min;
  };
  return (
    <div className="cantidad">
      <button type="button" className="paso" disabled={valor <= min}
        onClick={() => onCambio(Math.max(min, valor - 1))}
        aria-label={`Disminuir ${etiqueta}`}>−</button>
      <input
        id={id} type="number" min={min} step={decimal ? 'any' : 1}
        inputMode={decimal ? 'decimal' : 'numeric'} value={valor}
        aria-label={etiqueta}
        onFocus={(e) => e.target.select()}
        onChange={(e) => onCambio(parsear(e.target.value))} />
      <button type="button" className="paso"
        onClick={() => onCambio(valor + 1)}
        aria-label={`Aumentar ${etiqueta}`}>+</button>
    </div>
  );
}

const Facturacion = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  // Datos maestros
  const [productos, setProductos] = useState([]);
  const [bebidas, setBebidas] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [ventasHistorial, setVentasHistorial] = useState([]);

  // Carrito. El total NO es estado: se deriva de aquí en cada render.
  const [carrito, setCarrito] = useState(() => {
    try {
      const guardado = localStorage.getItem('carrito');
      return guardado ? JSON.parse(guardado) : [];
    } catch {
      return [];
    }
  });

  // Datos de la venta
  const [cliente, setCliente] = useState('');
  const [metodoPago, setMetodoPago] = useState('Efectivo');
  const [empleadoSeleccionado, setEmpleadoSeleccionado] = useState('');
  const [guardando, setGuardando] = useState(false);

  // Modal de ingredientes
  const [modalAbierto, setModalAbierto] = useState(false);
  const [productoActual, setProductoActual] = useState(null);
  const [ingredientes, setIngredientes] = useState([]);
  const [cantidadProductos, setCantidadProductos] = useState(1);

  const [busquedaVenta, setBusquedaVenta] = useState('');

  const [opcionProducto, setOpcionProducto] = useState(null);
  const [opcionBebida, setOpcionBebida] = useState(null);

  const refModal = useRef(null);
  const refSelectProducto = useRef(null);

  const total = useMemo(
    () => carrito.reduce((suma, i) => suma + Number(i.price) * i.cantidad, 0),
    [carrito]
  );
  const unidades = useMemo(
    () => carrito.reduce((suma, i) => suma + i.cantidad, 0),
    [carrito]
  );

  useEffect(() => {
    localStorage.setItem('carrito', JSON.stringify(carrito));
    // La clave antigua guardaba un total paralelo que podía desajustarse.
    localStorage.removeItem('total');
  }, [carrito]);

  /* ------------------------------------------------------------ datos ---- */

  const pedir = useCallback(async (ruta, set, queFalla) => {
    try {
      const { data } = await axios.get(`${apiUrl}${ruta}`);
      set(data);
    } catch {
      toast.error(`Error al obtener ${queFalla}`);
    }
  }, [apiUrl]);

  const fetchHistorial = useCallback(async () => {
    try {
      const { data } = await axios.get(`${apiUrl}/api/ventas`);
      const ordenadas = [...data].sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
      setVentasHistorial(ordenadas);
    } catch {
      toast.error('Error al obtener historial de ventas');
    }
  }, [apiUrl]);

  useEffect(() => {
    pedir('/api/productos', setProductos, 'productos');
    pedir('/api/bebidas', setBebidas, 'bebidas');
    pedir('/api/empleados/empleados', setEmpleados, 'empleados');
    fetchHistorial();
  }, [pedir, fetchHistorial]);

  const opcionesProductos = useMemo(
    () => productos.map((p) => ({ value: p.producto_id, label: `${p.name} — ${dinero(p.price)}`, data: p })),
    [productos]
  );
  const opcionesBebidas = useMemo(
    () => bebidas.map((b) => ({ value: b.bebida_id, label: `${b.name} — ${dinero(b.price)}`, data: b })),
    [bebidas]
  );

  /* ---------------------------------------------------------- carrito ---- */

  const agregarAlCarrito = useCallback((item) => {
    const firma = firmaIngredientes(item.ingredientesSeleccionados);
    const clave = item.type === 'producto' ? 'producto_id' : 'bebida_id';

    setCarrito((prev) => {
      const i = prev.findIndex(
        (l) =>
          l.type === item.type &&
          l[clave] === item[clave] &&
          firmaIngredientes(l.ingredientesSeleccionados) === firma
      );
      if (i === -1) return [...prev, { ...item, uid: nuevaLinea() }];
      const copia = [...prev];
      copia[i] = { ...copia[i], cantidad: copia[i].cantidad + item.cantidad };
      return copia;
    });
    toast.success(`${item.name} agregado`);
  }, []);

  const cambiarCantidad = (uid, delta) =>
    setCarrito((prev) =>
      prev.map((l) => (l.uid === uid ? { ...l, cantidad: Math.max(1, l.cantidad + delta) } : l))
    );

  const quitarLinea = (uid) => {
    setCarrito((prev) => prev.filter((l) => l.uid !== uid));
    toast.warn('Ítem eliminado');
  };

  const vaciarCarrito = () => {
    if (carrito.length && window.confirm('¿Vaciar el carrito completo?')) setCarrito([]);
  };

  /* ------------------------------------------------------------ modal ---- */

  const abrirModal = (producto) => {
    setProductoActual(producto);
    setCantidadProductos(1);
    setIngredientes(
      (producto.Ingredientes || []).map((ing) => ({
        ...ing,
        checked: true,
        amount: ing.ProductoIngrediente?.amount || 1,
      }))
    );
    setModalAbierto(true);
  };

  const cerrarModal = useCallback(() => {
    setModalAbierto(false);
    setProductoActual(null);
    setOpcionProducto(null);
    // Devolver el foco al buscador permite encadenar ventas sin tocar el mouse.
    setTimeout(() => refSelectProducto.current?.focus(), 0);
  }, []);

  // Escape cierra y Tab queda atrapado dentro del modal.
  useEffect(() => {
    if (!modalAbierto) return;
    const enfocables = () =>
      refModal.current?.querySelectorAll(
        'button, input, select, textarea, [href], [tabindex]:not([tabindex="-1"])'
      ) || [];
    enfocables()[0]?.focus();

    const alPulsar = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); cerrarModal(); return; }
      if (e.key !== 'Tab') return;
      const lista = Array.from(enfocables());
      if (!lista.length) return;
      const primero = lista[0];
      const ultimo = lista[lista.length - 1];
      if (e.shiftKey && document.activeElement === primero) { e.preventDefault(); ultimo.focus(); }
      else if (!e.shiftKey && document.activeElement === ultimo) { e.preventDefault(); primero.focus(); }
    };
    document.addEventListener('keydown', alPulsar);
    return () => document.removeEventListener('keydown', alPulsar);
  }, [modalAbierto, cerrarModal]);

  const confirmarIngredientes = () => {
    if (!productoActual) return;
    agregarAlCarrito({
      ...productoActual,
      type: 'producto',
      producto_id: productoActual.producto_id,
      bebida_id: null,
      ingredientesSeleccionados: ingredientes
        .filter((i) => i.checked && i.amount > 0)
        .map(({ ingredient_id, name, amount }) => ({ ingredient_id, name, amount })),
      cantidad: cantidadProductos,
      price: productoActual.price,
    });
    cerrarModal();
  };

  // Las bebidas no tienen ingredientes: entran directo al carrito.
  useEffect(() => {
    if (!opcionBebida) return;
    const b = opcionBebida.data;
    agregarAlCarrito({
      ...b, type: 'bebida', bebida_id: b.bebida_id, producto_id: null,
      cantidad: 1, price: b.price, ingredientesSeleccionados: [],
    });
    setOpcionBebida(null);
  }, [opcionBebida, agregarAlCarrito]);

  /* ------------------------------------------------------------ venta ---- */

  const confirmarVenta = async () => {
    if (!empleadoSeleccionado) return toast.error('Selecciona el vendedor.');
    if (carrito.length === 0) return toast.error('El carrito está vacío.');

    setGuardando(true);
    try {
      await axios.post(`${apiUrl}/api/ventas`, {
        cliente: cliente.trim() || CLIENTE_POR_DEFECTO,
        metodo_pago: metodoPago,
        vendedor_id: empleadoSeleccionado,
        fecha: new Date().toISOString(),
        total,
        detalles: carrito.map((item) => ({
          tipo_producto: item.type,
          producto_id: item.type === 'producto' ? item.producto_id : null,
          bebida_id: item.type === 'bebida' ? item.bebida_id : null,
          cantidad: item.cantidad,
          precio: item.price,
          ingredientes: item.ingredientesSeleccionados || [],
        })),
      });
      toast.success(`Venta registrada por ${dinero(total)}`);
      setCarrito([]);
      setCliente('');
      setMetodoPago('Efectivo');
      fetchHistorial();
    } catch {
      toast.error('Error al registrar la venta');
    } finally {
      setGuardando(false);
    }
  };

  // Historial completo. El filtro recorre lo que ya está en memoria, así que
  // buscar no vuelve a pegarle al servidor.
  const ventasFiltradas = useMemo(() => {
    const q = busquedaVenta.trim().toLowerCase();
    if (!q) return ventasHistorial;
    return ventasHistorial.filter((v) =>
      String(v.ventas_id).includes(q) ||
      (v.cliente || '').toLowerCase().includes(q) ||
      (v.metodo_pago || '').toLowerCase().includes(q) ||
      new Date(v.fecha).toLocaleString('es-CO').toLowerCase().includes(q)
    );
  }, [ventasHistorial, busquedaVenta]);

  /* ----------------------------------------------------------- render ---- */

  return (
    <div className="caja" data-densidad="caja">
      <ToastContainer position="top-right" autoClose={2200} />

      <div className="encabezado-pagina">
        <div>
          <h1>Facturación</h1>
          <p className="subtitulo">Registra una venta y emite la factura.</p>
        </div>
      </div>

      <div className="caja-cuerpo">
        {/* --------------------------------------------------- izquierda */}
        <div className="caja-catalogo">
          <section className="tarjeta-base">
            <h2>Agregar al pedido</h2>
            <div className="caja-buscadores">
              <div className="campo">
                <label htmlFor="buscar-producto">Producto</label>
                <Select
                  inputId="buscar-producto"
                  ref={refSelectProducto}
                  classNamePrefix="react-select"
                  options={opcionesProductos}
                  placeholder="Busca un producto…"
                  isSearchable
                  value={opcionProducto}
                  noOptionsMessage={() => 'Sin resultados'}
                  onChange={(opt) => { if (opt) { setOpcionProducto(opt); abrirModal(opt.data); } }}
                />
              </div>
              <div className="campo">
                <label htmlFor="buscar-bebida">Bebida</label>
                <Select
                  inputId="buscar-bebida"
                  classNamePrefix="react-select"
                  options={opcionesBebidas}
                  placeholder="Busca una bebida…"
                  isSearchable
                  value={opcionBebida}
                  noOptionsMessage={() => 'Sin resultados'}
                  onChange={setOpcionBebida}
                />
              </div>
            </div>
          </section>

          <section className="tarjeta-base">
            <div className="encabezado-seccion">
              <h2>Historial de facturación</h2>
              <span className="texto-auxiliar">
                {busquedaVenta
                  ? `${ventasFiltradas.length} de ${ventasHistorial.length}`
                  : `${ventasHistorial.length} ventas`}
              </span>
            </div>

            <div className="campo buscador-ventas">
              <label htmlFor="buscar-venta" className="solo-lectores">Buscar venta</label>
              <input id="buscar-venta" type="search" value={busquedaVenta}
                placeholder="Buscar por número, cliente, método o fecha…"
                onChange={(e) => setBusquedaVenta(e.target.value)} />
            </div>

            {ventasFiltradas.length === 0 ? (
              <div className="estado-vacio">
                <h3>{busquedaVenta ? 'Sin coincidencias' : 'Todavía no hay ventas'}</h3>
                <p>{busquedaVenta ? 'Prueba con otro término.' : 'La primera aparecerá aquí.'}</p>
              </div>
            ) : (
              <div className="tabla-scroll historial-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>#</th><th>Fecha</th><th>Cliente</th><th>Método</th>
                      <th className="cifra">Total</th><th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasFiltradas.map((v) => (
                      <tr key={v.ventas_id}>
                        <td>{v.ventas_id}</td>
                        <td>{new Date(v.fecha).toLocaleString('es-CO', { dateStyle: 'short', timeStyle: 'short' })}</td>
                        <td className="celda-cliente">{v.cliente || '—'}</td>
                        <td><span className="distintivo neutro">{v.metodo_pago || '—'}</span></td>
                        <td className="cifra">{dinero(v.total)}</td>
                        <td>
                          <div className="acciones-fila">
                            <Link to={`/facturacion/imprimir/${v.ventas_id}`} className="enlace-boton">Imprimir</Link>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        {/* ----------------------------------------------------- derecha */}
        <aside className="caja-ticket">
          <section className="tarjeta-base ticket">
            <div className="encabezado-seccion">
              <h2>Pedido</h2>
              {carrito.length > 0 && (
                <button className="fantasma pequeno" onClick={vaciarCarrito}>Vaciar</button>
              )}
            </div>

            {carrito.length === 0 ? (
              <div className="estado-vacio"><h3>Carrito vacío</h3><p>Busca un producto para empezar.</p></div>
            ) : (
              <ul className="renglones">
                {carrito.map((item) => (
                  <li key={item.uid} className="renglon">
                    <div className="renglon-info">
                      <span className="renglon-nombre">{item.name}</span>
                      <span className="renglon-precio">{dinero(item.price)} c/u</span>
                      {item.ingredientesSeleccionados?.length > 0 && (
                        <span className="renglon-ingredientes">
                          {item.ingredientesSeleccionados.map((g) => `${g.name}${g.amount > 1 ? ` ×${g.amount}` : ''}`).join(', ')}
                        </span>
                      )}
                    </div>
                    <div className="contador">
                      <button className="paso" onClick={() => cambiarCantidad(item.uid, -1)}
                        disabled={item.cantidad <= 1} aria-label={`Quitar uno de ${item.name}`}>−</button>
                      <span className="contador-valor">{item.cantidad}</span>
                      <button className="paso" onClick={() => cambiarCantidad(item.uid, 1)}
                        aria-label={`Agregar uno de ${item.name}`}>+</button>
                    </div>
                    <span className="renglon-total">{dinero(item.price * item.cantidad)}</span>
                    <button className="quitar" onClick={() => quitarLinea(item.uid)}
                      aria-label={`Eliminar ${item.name}`}>×</button>
                  </li>
                ))}
              </ul>
            )}

            <div className="total-caja">
              <span className="total-etiqueta">Total{unidades > 0 ? ` · ${unidades} ${unidades === 1 ? 'unidad' : 'unidades'}` : ''}</span>
              <strong className="total-monto">{dinero(total)}</strong>
            </div>

            <div className="campo">
              <label id="etiqueta-pago">Método de pago</label>
              <div className="metodos" role="group" aria-labelledby="etiqueta-pago">
                {METODOS_PAGO.map((m) => (
                  <button key={m} className={`metodo ${metodoPago === m ? 'activo' : ''}`}
                    aria-pressed={metodoPago === m} onClick={() => setMetodoPago(m)}>{m}</button>
                ))}
              </div>
            </div>

            <div className="campo">
              <label htmlFor="vendedor">Vendedor</label>
              <select id="vendedor" value={empleadoSeleccionado}
                onChange={(e) => setEmpleadoSeleccionado(e.target.value)}>
                <option value="">Selecciona…</option>
                {empleados.map((emp) => (
                  <option key={emp.empleado_id} value={emp.empleado_id}>{emp.nombre} {emp.apellido}</option>
                ))}
              </select>
            </div>

            <div className="campo">
              <label htmlFor="cliente">Cliente</label>
              <input id="cliente" type="text" value={cliente} placeholder={CLIENTE_POR_DEFECTO}
                onChange={(e) => setCliente(e.target.value)} />
              <span className="ayuda">Opcional. Si lo dejas vacío se registra como “{CLIENTE_POR_DEFECTO}”.</span>
            </div>

            <button className="grande bloque" onClick={confirmarVenta}
              disabled={carrito.length === 0 || guardando} data-ocupado={guardando}>
              {guardando ? 'Registrando…' : `Cobrar ${dinero(total)}`}
            </button>
          </section>
        </aside>
      </div>

      {/* ------------------------------------------------------- modal */}
      {modalAbierto && productoActual && (
        <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) cerrarModal(); }}>
          <div className="modal-content" ref={refModal} role="dialog" aria-modal="true"
            aria-label={`Ingredientes de ${productoActual.name}`}>
            <h2>{productoActual.name}</h2>
            <p className="texto-auxiliar">Desmarca lo que el cliente no quiera.</p>

            <div className="ingredientes-list">
              {ingredientes.length === 0 && <p className="texto-auxiliar">Este producto no lleva ingredientes configurables.</p>}
              {ingredientes.map((ing) => (
                <div key={ing.ingredient_id} className="ingrediente-item">
                  <label className="ingrediente-nombre">
                    <input type="checkbox" checked={ing.checked}
                      onChange={() => setIngredientes((prev) => prev.map((x) =>
                        x.ingredient_id === ing.ingredient_id ? { ...x, checked: !x.checked } : x))} />
                    <span>{ing.name}</span>
                  </label>
                  <Cantidad
                    valor={ing.amount} decimal
                    etiqueta={`cantidad de ${ing.name}`}
                    onCambio={(n) => setIngredientes((prev) => prev.map((x) =>
                      x.ingredient_id === ing.ingredient_id ? { ...x, amount: n } : x))} />
                </div>
              ))}
            </div>

            <div className="modal-pie">
              <div className="campo campo-cantidad">
                <label htmlFor="cantidad-producto">Cantidad</label>
                <Cantidad id="cantidad-producto" valor={cantidadProductos}
                  etiqueta="cantidad de productos" onCambio={setCantidadProductos} />
                <div className="atajos-cantidad">
                  {[1, 2, 3, 4, 5, 6].map((n) => (
                    <button type="button" key={n}
                      className={`atajo ${cantidadProductos === n ? 'activo' : ''}`}
                      aria-pressed={cantidadProductos === n}
                      onClick={() => setCantidadProductos(n)}>{n}</button>
                  ))}
                </div>
              </div>
              <div className="modal-botones">
                <button className="cancelar" onClick={cerrarModal}>Cancelar</button>
                <button onClick={confirmarIngredientes}>
                  Agregar {dinero(productoActual.price * cantidadProductos)}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Facturacion;
